/**
 * DailyTrendActivitySection - Daily Trend + Activity Display for Dashboard (Part 6)
 *
 * Layout: Left = Daily Trend Charts, Right = Daily Activities
 * - One trend chart per day, vertically arranged
 * - Activities grouped by date (insulin, food, exercise, water, urination, vomiting, bgm)
 * - Left and right columns aligned by date
 */

import { Box, Button, Grid, Paper, Skeleton, Typography } from '@mui/material'
import { format, parseISO } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import { useGetList, useStore, useTranslate } from 'react-admin'
import {
  calculateAdaptiveGapThresholdMs,
  formatGlucoseForDisplay,
  GLUCOSE_CAP_COLOR,
  GLUCOSE_VALID_RANGE,
  getFixedGlucoseAxis,
  isGlucoseOutOfRange,
  prepareGlucoseChartSeries,
} from '../../common'
import { LazyPlot as Plot } from '../../common/LazyPlot'
import ToneChip from '../../components/ToneChip'
import { useDeviceMetrics } from '../../hooks/useDeviceMetrics'
import {
  DailyActivityGroup,
  DailyTrendData,
  GLUCOSE_THRESHOLDS,
  TIR_ZONE_COLORS,
} from '../../types/dashboard'
import { ACTIVITY_MARKER_COLORS } from '../../utils/activityConstants'
import { resolveActivityDisplayTime } from '../../utils/activityTime'
import { getCanonicalActivityType } from '../../utils/activityType'
import { formatActivityDescription as formatActivityDescriptionUtil } from '../../utils/activityUtils'
import { convertGlucose, GlucoseUnit } from '../../utils/glucoseUnits'
import { useDateFormatter } from '../../utils/localeDate'
import { SHARED_METRICS_PER_PAGE } from '../../utils/paginationUtils'

// Minimum valid timestamp: January 1, 2020 (filter out obviously invalid dates like 1973)
const MIN_VALID_TIMESTAMP = new Date('2020-01-01T00:00:00Z').getTime() / 1000

// Activity icons as emojis for visual clarity
const ACTIVITY_ICONS: Record<string, string> = {
  insulin: '💉',
  food: '🍽️',
  feeding: '🍽️',
  exercise: '🏃',
  water: '💧',
  urination: '🚽',
  vomiting: '🤮',
  bgm: '🩸',
  medicine: '💊',
  sleep: '😴',
  hba1c: '🧪',
  weight: '⚖️',
  signsOfIllness: '⚠️',
}

// Use ACTIVITY_MARKER_COLORS from utils/activityConstants.ts for consistent colors
// DO NOT define local ACTIVITY_COLORS - always use the centralized constants

export interface DailyTrendActivitySectionProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  chartHeight?: number
  targetMin?: number
  targetMax?: number
  /**
   * Upper bound of the High zone (above this = Very High), from the pet's
   * species range — dogs 22.2, cats 27.8. MUST be the same cut the TIR capsule
   * and the trend chart use, or the same reading is a red point here and an
   * orange one there (eng-brain P14-1). Falls back to the flat threshold.
   */
  highMax?: number
  linkedHighlightRange?: { start: Date; end: Date } | null
}

export const DailyTrendActivitySection: React.FC<DailyTrendActivitySectionProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    chartHeight = 150,
    targetMin = GLUCOSE_THRESHOLDS.TARGET_MIN,
    targetMax = GLUCOSE_THRESHOLDS.TARGET_MAX,
    highMax = GLUCOSE_THRESHOLDS.HIGH,
    linkedHighlightRange = null,
  }) => {
    const translate = useTranslate()
    const fmtDate = useDateFormatter()
    const [storeDeviceId] = useStore<any>('selectedDeviceId')
    const [storePetId] = useStore<any>('selectedPetId')
    const [glucoseUnit] = useStore('glucoseUnit', 'mmol/L')
    const [groupResetVersion, setGroupResetVersion] = useState(0)
    const [chartDirtyState, setChartDirtyState] = useState<Record<string, boolean>>({})
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId
    const displayGlucoseUnit = glucoseUnit as GlucoseUnit

    // Mask out-of-range highs/lows as ">30 mmol/L" / "<2.2 mmol/L" so the daily
    // stats agree with the Glucose Trend chart (no raw "30.9 mmol/L" leak).
    const formatDailySummaryValue = (mmolValue: number) =>
      formatGlucoseForDisplay(mmolValue, displayGlucoseUnit)

    // Use the unified metrics hook
    const {
      data: metricsData = [],
      isLoading: metricsLoading,
      error: metricsError,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'ASC' },
      pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
    })

    // Fetch activities for the pet
    const startTimestamp = startDate ? Math.floor(startDate.getTime() / 1000) : undefined
    const endTimestamp = endDate ? Math.floor(endDate.getTime() / 1000) : undefined

    const {
      data: activities = [],
      isLoading: activitiesLoading,
      error: activitiesError,
    } = useGetList(
      'activities',
      {
        // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
        meta: { params: { depth: 0 } },
        filter: petId
          ? {
              pet: petId,
              ...(startTimestamp && { start_time_gte: startTimestamp }),
              ...(endTimestamp && { start_time_lte: endTimestamp }),
            }
          : {},
        sort: { field: 'start_time', order: 'DESC' },
        pagination: { page: 1, perPage: 1000 },
      },
      { enabled: !!petId },
    )

    // Process metrics into daily data
    const dailyTrends = useMemo((): DailyTrendData[] => {
      if (!metricsData || metricsData.length === 0) return []

      // Apply BGM correction + clamping before smoothing (same pipeline as GlucoseTrendChart)
      const filteredMetrics = prepareGlucoseChartSeries(metricsData)
      if (filteredMetrics.length === 0) return []

      // Group by date
      const grouped: Map<
        string,
        { readings: Array<{ timestamp: Date; glucose: number }>; values: number[] }
      > = new Map()

      filteredMetrics.forEach((metric) => {
        if (!metric.timestamp) return
        // Filter out invalid timestamps (before 2020) - catches device clock issues
        if (metric.timestamp < MIN_VALID_TIMESTAMP) return
        const timestamp = new Date(metric.timestamp * 1000)
        const dateKey = format(timestamp, 'yyyy-MM-dd')

        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, { readings: [], values: [] })
        }

        const group = grouped.get(dateKey)!
        group.readings.push({ timestamp, glucose: metric.glucose })
        group.values.push(metric.glucose)
      })

      // Convert to array and calculate stats
      return Array.from(grouped.entries())
        .map(([dateKey, { readings, values }]) => ({
          date: parseISO(dateKey),
          readings: readings.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
          min: Math.min(...values),
          max: Math.max(...values),
          avg: values.reduce((a, b) => a + b, 0) / values.length,
          readingCount: values.length,
        }))
        .sort((a, b) => b.date.getTime() - a.date.getTime()) // Newest first
    }, [metricsData])

    // Group activities by date
    const dailyActivities = useMemo((): Map<string, DailyActivityGroup> => {
      const grouped = new Map<string, DailyActivityGroup>()

      activities.forEach((activity: any) => {
        const timestamp = resolveActivityDisplayTime(activity)
        if (!timestamp) return
        // Filter out activities with invalid timestamps (before 2020)
        if (timestamp.getTime() / 1000 < MIN_VALID_TIMESTAMP) return
        const dateKey = format(timestamp, 'yyyy-MM-dd')

        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, {
            date: parseISO(dateKey),
            activities: {},
            totalCount: 0,
          })
        }

        const group = grouped.get(dateKey)!
        const activityType = getCanonicalActivityType(activity.type)
        const entry = {
          id: activity.id,
          time: timestamp,
          type: activity.type,
          description: formatActivityDescriptionUtil(activity, translate),
        }

        // Bucket by canonical type. Dynamic so any activity type the
        // mobile app emits (medicine, sleep, hba1c, weight,
        // signsOfIllness, other, …) automatically gets a panel row.
        // The renderer (Object.entries(activities)) is already dynamic;
        // icons + colors fall back to '📝' + gray for any type without
        // explicit styling, but most known types are mapped below.
        if (!group.activities[activityType]) {
          group.activities[activityType] = []
        }
        group.activities[activityType].push(entry)

        group.totalCount++
      })

      return grouped
    }, [activities, translate])

    const activeChartKeys = useMemo(
      () => new Set(dailyTrends.map((day) => format(day.date, 'yyyy-MM-dd'))),
      [dailyTrends],
    )

    useEffect(() => {
      setChartDirtyState((prev) => {
        const nextEntries = Object.entries(prev).filter(([chartKey]) =>
          activeChartKeys.has(chartKey),
        )
        if (nextEntries.length === Object.keys(prev).length) return prev
        return Object.fromEntries(nextEntries)
      })
    }, [activeChartKeys])

    const handleChartDirtyChange = (chartKey: string, isDirty: boolean) => {
      setChartDirtyState((prev) => {
        if ((prev[chartKey] ?? false) === isDirty) return prev
        return { ...prev, [chartKey]: isDirty }
      })
    }

    const hasGroupDirtyState = Object.values(chartDirtyState).some(Boolean)

    const isLoading = metricsLoading || activitiesLoading

    if (isLoading) {
      return (
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={chartHeight + 50} sx={{ mb: 2 }} />
          ))}
        </Box>
      )
    }

    if (metricsError) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
        </Box>
      )
    }

    if (dailyTrends.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {translate('pos.dashboard.no_data_available')}
          </Typography>
        </Box>
      )
    }

    return (
      <Box>
        {/* Only the metrics error used to be read: a failed activities request
            showed every day with no insulin/meal events, indistinguishable from
            "nothing recorded". Same caption as the trend chart (eng-brain pass 78). */}
        {activitiesError && (
          <Box sx={{ mb: 1 }}>
            <ToneChip
              tone="attention"
              label={translate('pos.glucose.activity_markers_load_error')}
            />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setGroupResetVersion((prev) => prev + 1)
              setChartDirtyState({})
            }}
            disabled={!hasGroupDirtyState}
          >
            {translate('pos.glucose.reset_group', { _: 'Reset group' })}
          </Button>
        </Box>
        {dailyTrends.map((day, index) => {
          const dateKey = format(day.date, 'yyyy-MM-dd')
          const dayActivities = dailyActivities.get(dateKey)

          return (
            <Paper key={dateKey} sx={{ mb: 2, p: 2 }} elevation={0} variant="outlined">
              {/* Date Header */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {fmtDate(day.date, 'long')}
              </Typography>

              <Grid container spacing={2}>
                {/* Left: Daily Trend Chart */}
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, bgcolor: 'white' }}
                  >
                    <DailyMiniChart
                      chartKey={dateKey}
                      data={day}
                      height={chartHeight}
                      targetMin={targetMin}
                      targetMax={targetMax}
                      highMax={highMax}
                      activities={dayActivities}
                      glucoseUnit={glucoseUnit}
                      groupResetVersion={groupResetVersion}
                      externalHighlightRange={linkedHighlightRange}
                      onDirtyChange={handleChartDirtyChange}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 2,
                        mt: 1,
                        px: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: 500, fontSize: '0.8rem' }}
                      >
                        {translate('pos.daily_trend.avg')}{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {formatDailySummaryValue(day.avg)}
                        </Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: 500, fontSize: '0.8rem' }}
                      >
                        {translate('pos.daily_trend.nadir')}{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {formatDailySummaryValue(day.min)}
                        </Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: 500, fontSize: '0.8rem' }}
                      >
                        {translate('pos.daily_trend.peak')}{' '}
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {formatDailySummaryValue(day.max)}
                        </Box>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ ml: { xs: 0, sm: 'auto' }, fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        {translate('pos.daily_trend.readings', { count: day.readingCount })}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Right: Daily Activities */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ height: chartHeight + 40, overflowY: 'auto' }}>
                    {dayActivities && dayActivities.totalCount > 0 ? (
                      <Box>
                        {Object.entries(dayActivities.activities).map(([type, entries]) => {
                          if (entries.length === 0) return null
                          return (
                            <Box key={type} sx={{ mb: 1.5 }}>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                              >
                                <Typography variant="caption" sx={{ fontSize: '1rem' }}>
                                  {ACTIVITY_ICONS[type] || '📝'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: ACTIVITY_MARKER_COLORS[type] || 'text.primary',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {type} ({entries.length})
                                </Typography>
                              </Box>
                              {entries.map((entry: any) => (
                                <Box
                                  key={entry.id}
                                  sx={{
                                    pl: 2.5,
                                    py: 0.25,
                                    borderLeft: `2px solid ${ACTIVITY_MARKER_COLORS[type] || '#ccc'}`,
                                    ml: 0.5,
                                  }}
                                >
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>{format(entry.time, 'HH:mm')}</strong>
                                    {entry.description && ` - ${entry.description}`}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )
                        })}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {translate('pos.activities.no_activities')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          )
        })}
      </Box>
    )
  },
)

// Use ACTIVITY_MARKER_COLORS imported from utils/activityConstants.ts
// DO NOT define local color constants - always use the centralized import

// Activity marker symbols for different types
const ACTIVITY_MARKER_SYMBOLS: Record<string, string> = {
  insulin: 'circle',
  food: 'diamond',
  feeding: 'diamond',
  exercise: 'triangle-up',
  water: 'square',
  urination: 'pentagon',
  vomiting: 'x',
  bgm: 'star',
  medicine: 'cross',
}

// Mini chart component for daily trends
interface DailyMiniChartProps {
  chartKey: string
  data: DailyTrendData
  height: number
  targetMin: number
  targetMax: number
  highMax: number
  activities?: DailyActivityGroup
  glucoseUnit: string
  groupResetVersion: number
  externalHighlightRange?: { start: Date; end: Date } | null
  onDirtyChange?: (chartKey: string, isDirty: boolean) => void
}

const DailyMiniChart: React.FC<DailyMiniChartProps> = ({
  chartKey,
  data,
  height,
  targetMin,
  targetMax,
  highMax,
  activities,
  glucoseUnit,
  groupResetVersion,
  externalHighlightRange = null,
  onDirtyChange,
}) => {
  const translate = useTranslate()
  const [chartResetVersion, setChartResetVersion] = useState(0)
  const [hasZoomOrPan, setHasZoomOrPan] = useState(false)
  const [hasSelection, setHasSelection] = useState(false)
  const [hasTransientHighlight, setHasTransientHighlight] = useState(false)
  const [selectedRange, setSelectedRange] = useState<{ start: Date; end: Date } | null>(null)
  const xValues = data.readings.map((r) => r.timestamp)
  const yValues = data.readings.map((r) => r.glucose)
  const activeHighlightRange = externalHighlightRange ?? selectedRange
  const isResetEnabled = hasZoomOrPan || hasSelection || hasTransientHighlight || !!selectedRange

  // Conversion via the shared display hook's pure helper (unit resolved above).
  const convertValue = (mmolValue: number) => convertGlucose(mmolValue, glucoseUnit as GlucoseUnit)

  // Convert Y values based on unit preference
  const convertedYValues = yValues.map(convertValue)
  const convertedTargetMin = convertValue(targetMin)
  const convertedTargetMax = convertValue(targetMax)

  // Display bounds (in the active unit) used to grey-cap out-of-range stretches,
  // matching GlucoseTrendChart: highs above 30 mmol/L (540 mg/dL) and lows below
  // 2.2 mmol/L (40 mg/dL) ride the boundary in grey instead of plotting their
  // (clinically meaningless) exact value.
  const displayCeil = getFixedGlucoseAxis(glucoseUnit).max
  const displayFloor = convertValue(GLUCOSE_VALID_RANGE.min)
  const clampToAxis = (converted: number) =>
    Math.min(Math.max(converted, displayFloor), displayCeil)

  // Get Y-axis max based on glucose unit, but never exceed the out-of-range
  // ceiling — out-of-range points are clamped to displayCeil, so the axis should
  // top out there (otherwise a 30.9 reading would push the axis past the cap).
  const dynamicYMax =
    glucoseUnit === 'mg/dL'
      ? Math.max(360, convertValue(data.max) + 36)
      : Math.max(20, data.max + 2)
  const yAxisMax = Math.min(displayCeil, dynamicYMax)

  // Helper to get TIR zone color for a glucose value (in mmol/L). Out-of-range
  // highs/lows are greyed (cap color), everything else keeps its TIR zone color.
  const getPointColor = (mmolValue: number): string => {
    if (isGlucoseOutOfRange(mmolValue)) return GLUCOSE_CAP_COLOR
    if (mmolValue < GLUCOSE_THRESHOLDS.VERY_LOW) return TIR_ZONE_COLORS.veryLow
    if (mmolValue < targetMin) return TIR_ZONE_COLORS.low
    if (mmolValue <= targetMax) return TIR_ZONE_COLORS.target
    if (mmolValue <= highMax) return TIR_ZONE_COLORS.high
    return TIR_ZONE_COLORS.veryHigh
  }

  // Per-point hover text (mmol-based decision so out-of-range masks the number).
  const pointHoverText = (mmolValue: number): string =>
    isGlucoseOutOfRange(mmolValue)
      ? `${translate('pos.glucose.out_of_range')}<br>${formatGlucoseForDisplay(mmolValue, glucoseUnit)}`
      : formatGlucoseForDisplay(mmolValue, glucoseUnit)

  // Build segmented glucose traces colored by TIR zone. Out-of-range stretches
  // are clamped to the axis boundary (grey) and their tooltip shows "Out of
  // range" instead of the raw number — consistent with GlucoseTrendChart.
  const baseOpacity = activeHighlightRange ? 0.35 : 1
  const hoverTpl = `%{x|%H:%M}<br>%{text}<extra></extra>`
  // Adaptive time-gap threshold — a gap wider than this breaks the line rather
  // than bridging missing data with a straight segment (matches
  // GlucoseTrendChart). Shared by the base and highlight segment builders.
  const gapThresholdMs = calculateAdaptiveGapThresholdMs(xValues)
  const glucoseSegments: Plotly.Data[] = (() => {
    const segments: Plotly.Data[] = []
    let segX: Date[] = []
    let segY: number[] = []
    let segText: string[] = []
    let segColor = ''

    const flush = () => {
      if (segX.length > 0) {
        segments.push({
          x: [...segX],
          y: [...segY],
          text: [...segText],
          type: 'scatter',
          mode: 'lines',
          line: { color: segColor, width: 1.5 },
          opacity: baseOpacity,
          showlegend: false,
          hovertemplate: hoverTpl,
        })
        segX = []
        segY = []
        segText = []
      }
    }

    // Break the line across missing data: if the gap since the previous point
    // exceeds the adaptive threshold, start a fresh segment (no connecting
    // point) instead of drawing a straight line across the hole — matching
    // GlucoseTrendChart, which never bridges gaps.
    for (let i = 0; i < xValues.length; i++) {
      const newColor = getPointColor(yValues[i])
      const plotted = clampToAxis(convertedYValues[i])
      const text = pointHoverText(yValues[i])
      const isGap =
        segX.length > 0 && xValues[i].getTime() - segX[segX.length - 1].getTime() > gapThresholdMs
      if (isGap) {
        flush()
        segX = [xValues[i]]
        segY = [plotted]
        segText = [text]
        segColor = newColor
      } else if (newColor !== segColor && segX.length > 0) {
        // Carry the boundary point into both segments so the color change has no gap.
        segX.push(xValues[i])
        segY.push(plotted)
        segText.push(text)
        flush()
        segX = [xValues[i]]
        segY = [plotted]
        segText = [text]
        segColor = newColor
      } else {
        segX.push(xValues[i])
        segY.push(plotted)
        segText.push(text)
        if (segX.length === 1) segColor = newColor
      }
    }
    flush()
    return segments
  })()

  // Build highlight segments for selection range
  const selectionStartTime = activeHighlightRange ? activeHighlightRange.start.getTime() : null
  const selectionEndTime = activeHighlightRange ? activeHighlightRange.end.getTime() : null
  const highlightSegments: Plotly.Data[] = (() => {
    if (!activeHighlightRange || selectionStartTime === null || selectionEndTime === null) return []
    const segments: Plotly.Data[] = []
    let segX: Date[] = []
    let segY: number[] = []
    let segColor = ''

    const flush = () => {
      if (segX.length > 0) {
        segments.push({
          x: [...segX],
          y: [...segY],
          type: 'scatter',
          mode: 'lines',
          line: { color: segColor, width: 3 },
          showlegend: false,
          hoverinfo: 'skip',
        })
        segX = []
        segY = []
      }
    }

    for (let i = 0; i < xValues.length; i++) {
      const ts = xValues[i].getTime()
      if (ts < selectionStartTime || ts > selectionEndTime) {
        flush()
        continue
      }
      const newColor = getPointColor(yValues[i])
      const plotted = clampToAxis(convertedYValues[i])
      const isGap = segX.length > 0 && ts - segX[segX.length - 1].getTime() > gapThresholdMs
      if (isGap) {
        // Break across missing data instead of bridging it (same as the base line).
        flush()
        segX = [xValues[i]]
        segY = [plotted]
        segColor = newColor
      } else if (newColor !== segColor && segX.length > 0) {
        segX.push(xValues[i])
        segY.push(plotted)
        flush()
        segX = [xValues[i]]
        segY = [plotted]
        segColor = newColor
      } else {
        segX.push(xValues[i])
        segY.push(plotted)
        if (segX.length === 1) segColor = newColor
      }
    }
    flush()
    return segments
  })()

  // Helper function to find glucose value at a given time
  // Returns the glucose value of the closest reading within 5 minutes, or places at target range top
  // Values are returned in the selected glucose unit
  const findGlucoseAtTime = (activityTime: Date): number => {
    const activityTimestamp = activityTime.getTime()
    let closestReading = null
    let closestDistance = Infinity
    const maxDistanceMs = 5 * 60 * 1000 // 5 minutes

    for (let i = 0; i < data.readings.length; i++) {
      const readingTimestamp = data.readings[i].timestamp.getTime()
      const distance = Math.abs(readingTimestamp - activityTimestamp)
      if (distance < closestDistance) {
        closestDistance = distance
        closestReading = data.readings[i]
      }
    }

    // If closest reading is within 5 minutes, use its glucose value (converted),
    // clamped to the axis so a marker on an out-of-range reading stays on-chart.
    // Otherwise, place the marker at the top of the target range (converted)
    if (closestReading && closestDistance <= maxDistanceMs) {
      return clampToAxis(convertValue(closestReading.glucose))
    }
    return convertValue(targetMax + 1) // Slightly above target range for visibility
  }

  // Create activity marker traces
  const activityTraces: Plotly.Data[] = []
  if (activities) {
    const activityTypes = Object.entries(activities.activities) as [string, any[]][]

    for (const [type, entries] of activityTypes) {
      if (entries.length === 0) continue

      const markerX: Date[] = []
      const markerY: number[] = []
      const hoverTexts: string[] = []

      for (const entry of entries) {
        markerX.push(entry.time)
        markerY.push(findGlucoseAtTime(entry.time))

        // Build hover text
        const timeStr = format(entry.time, 'HH:mm')
        const typeName = type.charAt(0).toUpperCase() + type.slice(1)
        let hoverText = `<b>${typeName}</b><br>${timeStr}`
        if (entry.description) {
          hoverText += `<br>${entry.description}`
        }
        hoverTexts.push(hoverText)
      }

      activityTraces.push({
        x: markerX,
        y: markerY,
        type: 'scatter',
        mode: 'markers',
        name: type,
        marker: {
          color: ACTIVITY_MARKER_COLORS[type] || '#666',
          size: 10,
          symbol: ACTIVITY_MARKER_SYMBOLS[type] || 'circle',
          line: {
            color: 'white',
            width: 2,
          },
        },
        hovertemplate: '%{customdata}<extra></extra>',
        customdata: hoverTexts,
        showlegend: false,
      })
    }
  }

  const traces: Plotly.Data[] = [
    // Target range band
    {
      x: [xValues[0], xValues[xValues.length - 1]],
      y: [convertedTargetMin, convertedTargetMin],
      type: 'scatter',
      mode: 'lines',
      line: { width: 0 },
      showlegend: false,
      hoverinfo: 'skip',
    },
    {
      x: [xValues[0], xValues[xValues.length - 1]],
      y: [convertedTargetMax, convertedTargetMax],
      type: 'scatter',
      mode: 'lines',
      fill: 'tonexty',
      fillcolor: 'rgba(34, 197, 94, 0.1)',
      line: { width: 0 },
      showlegend: false,
      hoverinfo: 'skip',
    },
    // Glucose line segments (colored by TIR zone)
    ...glucoseSegments,
    // Highlight segments for selection range
    ...highlightSegments,
    // Activity markers (added after glucose line so they appear on top)
    ...activityTraces,
  ]

  const dayStart = new Date(data.date)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(data.date)
  dayEnd.setHours(23, 59, 59, 999)

  const highlightShapes: Array<Partial<Plotly.Shape>> = []
  if (activeHighlightRange) {
    highlightShapes.push(
      {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: activeHighlightRange.start,
        x1: activeHighlightRange.end,
        y0: 0,
        y1: 1,
        fillcolor: 'rgba(25, 118, 210, 0.12)',
        line: { width: 0 },
        layer: 'below',
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: activeHighlightRange.start,
        x1: activeHighlightRange.start,
        y0: 0,
        y1: 1,
        line: { color: 'rgba(25, 118, 210, 0.6)', width: 2, dash: 'dot' },
        layer: 'above',
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: activeHighlightRange.end,
        x1: activeHighlightRange.end,
        y0: 0,
        y1: 1,
        line: { color: 'rgba(25, 118, 210, 0.6)', width: 2, dash: 'dot' },
        layer: 'above',
      },
    )
  }

  const layout: Partial<Plotly.Layout> = {
    height,
    margin: { t: 10, b: 30, l: 40, r: 10 },
    xaxis: {
      tickformat: '%H:%M',
      showgrid: false,
      range: [dayStart, dayEnd],
      autorange: false,
      fixedrange: true,
    },
    yaxis: {
      range: [0, yAxisMax],
      showgrid: true,
      gridcolor: '#f0f0f0',
      autorange: false,
      fixedrange: true,
    },
    dragmode: 'select',
    showlegend: false,
    hovermode: 'closest', // Changed from 'x unified' to allow individual marker tooltips
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
    shapes: highlightShapes,
  }

  useEffect(() => {
    onDirtyChange?.(chartKey, isResetEnabled)
  }, [chartKey, isResetEnabled, onDirtyChange])

  useEffect(() => {
    setChartResetVersion((prev) => prev + 1)
    setHasZoomOrPan(false)
    setHasSelection(false)
    setHasTransientHighlight(false)
    setSelectedRange(null)
  }, [groupResetVersion])

  const handleRelayout = (eventData: Record<string, unknown>) => {
    const relayoutKeys = Object.keys(eventData || {})
    const hasAxisChange = relayoutKeys.some(
      (key) =>
        key.includes('xaxis.range') ||
        key.includes('yaxis.range') ||
        key.includes('xaxis.autorange') ||
        key.includes('yaxis.autorange'),
    )

    if (!hasAxisChange) return
    // Keep viewport immutable in daily trend charts; relayout should not mark zoom state.
    setHasZoomOrPan(false)
    // Force a remount so any Plotly-internal range mutation is discarded.
    setChartResetVersion((prev) => prev + 1)
  }

  const handleSelected = (eventData: any) => {
    const range = eventData?.range?.x
    if (Array.isArray(range) && range.length === 2) {
      const first = new Date(range[0])
      const second = new Date(range[1])
      if (!Number.isNaN(first.getTime()) && !Number.isNaN(second.getTime())) {
        const start = first <= second ? first : second
        const end = first <= second ? second : first
        setSelectedRange({ start, end })
        setHasSelection(true)
        // Clear Plotly's native selection viewport artifact; keep only custom highlight visuals.
        setChartResetVersion((prev) => prev + 1)
        return
      }
    }

    setHasSelection(true)
    setChartResetVersion((prev) => prev + 1)
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        size="small"
        variant="outlined"
        disabled={!isResetEnabled}
        sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, minWidth: 0, px: 1 }}
        onClick={() => {
          setChartResetVersion((prev) => prev + 1)
          setHasZoomOrPan(false)
          setHasSelection(false)
          setHasTransientHighlight(false)
          setSelectedRange(null)
        }}
      >
        {translate('pos.glucose.reset_view', { _: 'Reset view' })}
      </Button>
      <Plot
        key={`${chartKey}-${chartResetVersion}`}
        data={traces}
        layout={layout}
        onRelayout={handleRelayout}
        onSelected={handleSelected}
        onDeselect={() => {
          setHasSelection(false)
          setSelectedRange(null)
          setChartResetVersion((prev) => prev + 1)
        }}
        onHover={() => setHasTransientHighlight(true)}
        onUnhover={() => setHasTransientHighlight(false)}
        config={{
          displayModeBar: false,
          staticPlot: false,
          responsive: true,
          scrollZoom: false,
        }}
        style={{ width: '100%' }}
      />
    </Box>
  )
}

export default DailyTrendActivitySection
